/**
 * Format a price as odds
 * @param price Price to format
 * @returns Formatted odds string (e.g., "$2.50")
 */
export const formatOdds = (price: number | undefined | null): string => {
  if (price === undefined || price === null) {
    return '-';
  }
  return `$${price.toFixed(2)}`;
};

/**
 * Format a weight
 * @param weight Weight to format
 * @returns Formatted weight string (e.g., "58.5kg")
 */
export const formatWeight = (weight: number | undefined | null): string => {
  if (weight === undefined || weight === null) {
    return '-';
  }
  return `${weight.toFixed(1)}kg`;
};

/**
 * Format a margin
 * @param margin Margin to format
 * @returns Formatted margin string (e.g., "2.5L" or "NS" for nose)
 */
export const formatMargin = (margin: number | undefined | null): string => {
  if (margin === undefined || margin === null) {
    return '-';
  }
  
  if (margin === 0) {
    return 'Won';
  }
  
  if (margin < 0.1) {
    return 'NS';
  }
  
  return `${margin.toFixed(1)}L`;
};

/**
 * Format a distance
 * @param distance Distance to format
 * @returns Formatted distance string (e.g., "1200m")
 */
export const formatDistance = (distance: number | undefined | null): string => {
  if (distance === undefined || distance === null) {
    return '-';
  }
  return `${distance}m`;
};

/**
 * Format a time
 * @param time Time to format (in seconds)
 * @returns Formatted time string (e.g., "1:12.34")
 */
export const formatTime = (time: number | undefined | null): string => {
  if (time === undefined || time === null) {
    return '-';
  }
  
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  
  if (minutes === 0) {
    return seconds.toFixed(2);
  }
  
  return `${minutes}:${seconds.toFixed(2).padStart(5, '0')}`;
};

/**
 * Format money
 * @param amount Amount to format
 * @returns Formatted money string (e.g., "$1,234.56")
 */
export const formatMoney = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) {
    return '-';
  }
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Format a percentage
 * @param percentage Percentage to format
 * @returns Formatted percentage string (e.g., "12.34%")
 */
export const formatPercentage = (percentage: number | undefined | null): string => {
  if (percentage === undefined || percentage === null) {
    return '-';
  }
  return `${percentage.toFixed(2)}%`;
};

/**
 * Format an Australian phone number for display
 * @param phoneNumber Phone number to format
 * @returns Formatted phone number for display (e.g., "04XX XXX XXX")
 */
export const formatPhoneNumberForDisplay = (phoneNumber: string): string => {
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Handle Australian mobile numbers (starting with 04)
  if (digitsOnly.startsWith('04') && digitsOnly.length >= 10) {
    // Format as 04XX XXX XXX
    return `${digitsOnly.substring(0, 4)} ${digitsOnly.substring(4, 7)} ${digitsOnly.substring(7, 10)}`;
  }
  
  // Handle international format (remove the + for formatting)
  if (phoneNumber.startsWith('+') && digitsOnly.length >= 11) {
    // Format as +61 4XX XXX XXX (for Australian numbers)
    if (digitsOnly.startsWith('61') && digitsOnly.substring(2, 3) === '4') {
      return `+61 ${digitsOnly.substring(2, 5)} ${digitsOnly.substring(5, 8)} ${digitsOnly.substring(8, 11)}`;
    }
  }
  
  // Return original if no formatting applied
  return phoneNumber;
};

export interface CountryDialCode {
  name: string;
  code: string;
  dialCode: string;
  flag: string;
  placeholder: string;
}

export const TOP_COUNTRIES: CountryDialCode[] = [
  { name: 'Australia', code: 'AU', dialCode: '+61', flag: '🇦🇺', placeholder: '0412 345 678' },
  { name: 'New Zealand', code: 'NZ', dialCode: '+64', flag: '🇳🇿', placeholder: '021 123 4567' },
  { name: 'Hong Kong', code: 'HK', dialCode: '+852', flag: '🇭🇰', placeholder: '9123 4567' },
];

export const ALL_COUNTRIES: CountryDialCode[] = [
  ...TOP_COUNTRIES,
  { name: 'Canada', code: 'CA', dialCode: '+1', flag: '🇨🇦', placeholder: '(416) 555-0123' },
  { name: 'China', code: 'CN', dialCode: '+86', flag: '🇨🇳', placeholder: '138 0013 8000' },
  { name: 'France', code: 'FR', dialCode: '+33', flag: '🇫🇷', placeholder: '06 12 34 56 78' },
  { name: 'Germany', code: 'DE', dialCode: '+49', flag: '🇩🇪', placeholder: '0151 1234 5678' },
  { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳', placeholder: '98765 43210' },
  { name: 'Ireland', code: 'IE', dialCode: '+353', flag: '🇮🇪', placeholder: '085 123 4567' },
  { name: 'Japan', code: 'JP', dialCode: '+81', flag: '🇯🇵', placeholder: '090-1234-5678' },
  { name: 'Malaysia', code: 'MY', dialCode: '+60', flag: '🇲🇾', placeholder: '012-345 6789' },
  { name: 'Philippines', code: 'PH', dialCode: '+63', flag: '🇵🇭', placeholder: '0917 123 4567' },
  { name: 'Singapore', code: 'SG', dialCode: '+65', flag: '🇸🇬', placeholder: '9123 4567' },
  { name: 'South Africa', code: 'ZA', dialCode: '+27', flag: '🇿🇦', placeholder: '071 123 4567' },
  { name: 'South Korea', code: 'KR', dialCode: '+82', flag: '🇰🇷', placeholder: '010-1234-5678' },
  { name: 'UAE', code: 'AE', dialCode: '+971', flag: '🇦🇪', placeholder: '050 123 4567' },
  { name: 'United Kingdom', code: 'GB', dialCode: '+44', flag: '🇬🇧', placeholder: '07700 900123' },
  { name: 'United States', code: 'US', dialCode: '+1', flag: '🇺🇸', placeholder: '(201) 555-0123' },
];

/**
 * Expected local (national) length for a country, taken from its placeholder
 * @param dialCode Country dial code (e.g. '+61')
 * @returns Number of local digits, or undefined for an unknown country
 */
const expectedLocalLength = (dialCode?: string): number | undefined => {
  const country = ALL_COUNTRIES.find(c => c.dialCode === dialCode);
  if (!country) return undefined;
  const digits = country.placeholder.replace(/\D/g, '');
  return digits.startsWith('0') ? digits.length - 1 : digits.length;
};

/**
 * Is this a believable local number length for the country?
 * Allows a digit either side of the placeholder, since some countries run
 * variable-length numbers; unknown countries fall back to a generic range.
 */
const isPlausibleLocalLength = (length: number, dialCode?: string): boolean => {
  const expected = expectedLocalLength(dialCode);
  if (expected === undefined) return length >= 6 && length <= 15;
  return Math.abs(length - expected) <= 1;
};

/**
 * Reduce an entered number to its local digits for a country: strips
 * punctuation, a dial code the user typed themselves (e.g. "+61404211211"
 * or "0061404211211") and the national trunk "0".
 * @param phoneNumber Phone number as entered
 * @param dialCode Country dial code (e.g. '+61')
 * @returns Local digits only (e.g. "404211211")
 */
export const toLocalDigits = (phoneNumber: string, dialCode?: string): string => {
  let digits = phoneNumber.replace(/\D/g, '');
  const dialDigits = dialCode ? dialCode.replace(/\D/g, '') : '';
  const stripTrunk = (value: string) => (value.startsWith('0') ? value.substring(1) : value);

  // International access prefix typed instead of "+"
  if (dialDigits && digits.startsWith(`00${dialDigits}`)) {
    digits = digits.substring(2);
  }

  // Dial code typed on top of the country already selected. Only strip it when
  // what is left is a plausible local number for that country — some local
  // numbers legitimately start with their own dial code (an Indian mobile can
  // start 91, for instance).
  if (dialDigits && digits.startsWith(dialDigits)) {
    const withoutDialCode = stripTrunk(digits.substring(dialDigits.length));
    if (isPlausibleLocalLength(withoutDialCode.length, dialCode)) {
      digits = digits.substring(dialDigits.length);
    }
  }

  return stripTrunk(digits);
};

/**
 * Rewrite an entered number in the country's own local format for display,
 * e.g. an Australian "+61404211211" becomes "0404211211"
 * @param phoneNumber Phone number as entered
 * @param dialCode Country dial code (e.g. '+61')
 * @returns The number as that country writes it locally
 */
export const toLocalDisplay = (phoneNumber: string, dialCode?: string): string => {
  const localDigits = toLocalDigits(phoneNumber, dialCode);
  if (!localDigits) return phoneNumber;
  const country = ALL_COUNTRIES.find(c => c.dialCode === dialCode);
  const usesTrunkZero = country ? country.placeholder.trim().startsWith('0') : false;
  return usesTrunkZero ? `0${localDigits}` : localDigits;
};

/**
 * Convert phone number to international format for API
 * @param phoneNumber Phone number to convert
 * @param dialCode Optional country dial code (e.g. '+61'); defaults to Australian behaviour
 * @returns Phone number in international format (e.g., "+61412345678")
 */
export const formatPhoneNumberForAPI = (phoneNumber: string, dialCode?: string): string => {
  const hasPlus = phoneNumber.startsWith('+');
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // Country code provided — reduce to local digits then prepend dial code
  if (dialCode) {
    const cleanDialCode = dialCode.replace(/\D/g, '');

    // A full international number for some other country — leave it alone
    if (hasPlus && !digitsOnly.startsWith(cleanDialCode) && digitsOnly.length >= 10) {
      return `+${digitsOnly}`;
    }

    return `+${cleanDialCode}${toLocalDigits(phoneNumber, dialCode)}`;
  }

  // Already a full international number
  if (hasPlus && digitsOnly.length >= 10) {
    return `+${digitsOnly}`;
  }

  // Legacy Australian fallback
  if (digitsOnly.startsWith('04') && digitsOnly.length >= 10) {
    return `+61${digitsOnly.substring(1)}`;
  }

  return `+${digitsOnly}`;
};

/**
 * Validate an Australian phone number for UI display
 * @param phoneNumber Phone number to validate
 * @returns Error message or empty string if valid
 */
export const validatePhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return 'Phone number is required';

  // Remove all non-digit characters for validation
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // Only accept Australian mobile format (04XXXXXXXX)
  if (digitsOnly.startsWith('04') && digitsOnly.length === 10) {
    return '';
  }

  // If it starts with +61, suggest using the local format instead
  if (phoneNumber.startsWith('+') && digitsOnly.startsWith('61') &&
      digitsOnly.substring(2, 3) === '4' && digitsOnly.length === 11) {
    return 'Please enter your number in Australian format (04XX XXX XXX)';
  }

  return 'Please enter a valid Australian mobile number starting with 04';
};

/**
 * Validate a phone number for any supported country
 * @param phoneNumber Local phone number as entered
 * @param dialCode Country dial code (e.g. '+61')
 * @returns Error message or empty string if valid
 */
export const validatePhoneNumberForCountry = (phoneNumber: string, dialCode: string): string => {
  if (!phoneNumber) return 'Phone number is required';
  const localDigits = toLocalDigits(phoneNumber, dialCode);

  // Australian mobiles are exactly 04XX XXX XXX
  if (dialCode === '+61') {
    return /^4\d{8}$/.test(localDigits)
      ? ''
      : 'Please enter an Australian mobile number in the format 0404 211 211';
  }

  // ponytail: other countries are checked on length against their placeholder,
  // not on carrier prefixes — add a country rule when one actually misbehaves
  if (!isPlausibleLocalLength(localDigits.length, dialCode)) {
    const example = ALL_COUNTRIES.find(c => c.dialCode === dialCode)?.placeholder;
    return example
      ? `Please enter a valid mobile number for this country (e.g. ${example})`
      : 'Please enter a valid phone number';
  }

  return '';
};

/**
 * Validate a formatted international phone number (E.164)
 * @param formattedNumber Phone number in international format (e.g. "+61412345678")
 * @returns True if the number is a valid E.164 number
 */
export const isValidInternationalMobile = (formattedNumber: string): boolean => {
  if (!formattedNumber.startsWith('+')) return false;
  const digits = formattedNumber.substring(1).replace(/\D/g, '');

  // Australian numbers must be a complete mobile: +614 plus 8 digits
  if (digits.startsWith('61')) return /^614\d{8}$/.test(digits);

  return digits.length >= 7 && digits.length <= 15;
};

/**
 * Validate a formatted phone number against Australian standards for API/Cognito
 * @param formattedNumber Phone number in international format to validate
 * @returns True if the number conforms to Australian standards, false otherwise
 */
export const isValidAustralianMobile = (formattedNumber: string): boolean => {
  // Check if the number starts with +614
  if (!formattedNumber.startsWith('+614')) {
    return false;
  }

  // Check if the total length is exactly 12 characters (including the +)
  if (formattedNumber.length !== 12) {
    return false;
  }

  // Check if it only contains digits after the +
  const digitsOnly = formattedNumber.substring(1);
  if (!/^\d+$/.test(digitsOnly)) {
    return false;
  }

  return true;
};
